import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { Set } from '../../../ts/models/Set';
import { AddUsersOptionsAction } from '../../../ts/services/AddUsersOptionsAction';
import { EditGroupMembershipAction } from '../../../ts/services/EditGroupMembershipAction';
import { ForcePasswordUpdateAction } from '../../../ts/services/ForcePasswordUpdateAction';
import { RemoveUsersAction } from '../../../userManagement/services/RemoveUsersAction';
import { ResetLoginRateLimitingAction } from '../../../ts/services/ResetLoginRateLimitingAction';
import { SetSiteUsersSiteRoleAction } from '../../../userManagement/services/SetSiteUsersSiteRoleAction';
import { UpdateUserAuthenticationAction } from '../../../ts/services/UpdateUserAuthenticationAction';
import {
  SiteUserAddUsers, SiteUserEditGroupMembership, SiteUserEditSiteRole,
  SiteUserForcePasswordUpdate, SiteUserRemove, SiteUserResetLoginRateLimiting,
  SiteUserSetAuthentication
} from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';
import { SiteUserAddAction, SiteUserMenuAction } from '../../actions/spaces/SiteUserMenuActions';
import { ServiceLocator } from '../../ServiceLocator';
import { reloadSpace } from './content/RefreshContentSagas';

export function addUsers() {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<AddUsersOptionsAction>('AddUsersOptionsAction')
    .showAddSiteUsersOptionsDialog(context);
}

export function* handleAddUsers(action: SiteUserAddAction): SagaIterator {
  try {
    yield call(addUsers);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function removeUsers(users: ISiteUserInfo[]) {
  return RemoveUsersAction.showRemoveUsersFromSiteDialog(Set.fromIdItemArray(users));
}

export function* handleRemoveUsers(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(removeUsers, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    // This is to prevent error notifcation from showing up when the dialog is dismissed
    // To be fixed after TFSID 1121927
    return;
  }
}

export function editSiteRole(users: ISiteUserInfo[]) {
  return SetSiteUsersSiteRoleAction.showSetSiteUsersSiteRoleDialog(Set.fromIdItemArray(users));
}

export function* handleEditSiteRole(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(editSiteRole, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function editGroupMembership(users: ISiteUserInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<EditGroupMembershipAction>('EditGroupMembershipAction')
    .showEditGroupMembershipDialog(Set.fromIdItemArray(users), context);
}

export function* handleEditGroupMembership(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(editGroupMembership, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function setAuthentication(users: ISiteUserInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<UpdateUserAuthenticationAction>('UpdateUserAuthenticationAction')
    .showUpdateUserAuthenticationDialog(Set.fromIdItemArray(users), context);
}

export function* handleSetAuthentication(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(setAuthentication, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function forcePasswordUpdate(users: ISiteUserInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<ForcePasswordUpdateAction>('ForcePasswordUpdateAction')
    .onSiteUsers(context, Set.fromIdItemArray(users));
}

export function* handleForcePasswordUpdate(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(forcePasswordUpdate, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function resetLoginRateLimiting(users: ISiteUserInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<ResetLoginRateLimitingAction>('ResetLoginRateLimitingAction')
    .onSiteUsers(context, Set.fromIdItemArray(users));
}

export function* handleResetLoginRateLimiting(action: SiteUserMenuAction): SagaIterator {
  try {
    yield call(resetLoginRateLimiting, action.items);

    // Need to reload space for license bar updates
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootSiteUserMenuActionSaga(): SagaIterator {
  yield takeEvery(SiteUserEditGroupMembership, handleEditGroupMembership);
  yield takeEvery(SiteUserEditSiteRole, handleEditSiteRole);
  yield takeEvery(SiteUserRemove, handleRemoveUsers);
  yield takeEvery(SiteUserAddUsers, handleAddUsers);
  yield takeEvery(SiteUserSetAuthentication, handleSetAuthentication);
  yield takeEvery(SiteUserForcePasswordUpdate, handleForcePasswordUpdate);
  yield takeEvery(SiteUserResetLoginRateLimiting, handleResetLoginRateLimiting);
}
